/**
 * Copyright 2025 John Huikku Alienrobot LLC
 * www.ALIENROBOT.com
 */

import { PeerPanel } from './PeerPanel'
import { JudgePanel } from './JudgePanel'
import { ChevronDown } from 'lucide-react'
import type { PeerConfig, Project, ChatMessage } from '@/lib/storage'
import type { PeerResponse, JudgeResult } from '@/ui/stores/orchestrator-store'

interface RightSidebarProps {
  width: number
  peers: PeerConfig[]
  peerResponses: Record<string, PeerResponse | null>
  peerHeights: Record<string, number>
  judgeResult: JudgeResult | null
  judgeAutoSend: boolean
  judgeThreshold: number
  judgeNotes: string
  isEditingThreshold: boolean
  approvalSent: boolean
  isOrchestrating: boolean
  draggedPeer: string | null
  currentProject: Project | null
  messages: ChatMessage[]
  isCollapsed: boolean
  onToggleCollapse: () => void
  onTogglePeerCollapse: (peerId: string) => void
  onTogglePeerEnabled: (peerId: string) => void
  onOpenPeerSettings: (peerId: string) => void
  onCopyPeerResponse: (content: string) => void
  onInflatePeerResponse: (peerName: string, content: string) => void
  onSavePeerChat: (peerId: string, peerName: string, content: string) => void
  onSendPeerToOrchestrator: (peerName: string, content: string) => void
  onExpandPeer: (peerId: string) => void
  onPeerDragStart: (peerId: string) => void
  onPeerDrop: (targetPeerId: string, draggedPeerId: string) => void
  onPeerDragEnd: () => void
  onResizePeerStart: (peerId: string) => void
  onToggleJudgeAutoSend: () => void
  onSetJudgeThreshold: (threshold: number) => void
  onSetJudgeNotes: (notes: string) => void
  onSetEditingThreshold: (editing: boolean) => void
  onApproveJudge: () => Promise<void>
  onRequestRevisions: () => void
  onCopyJudge: () => void
  onInflateJudge: () => void
  onSaveJudge: () => void
  onSendJudgeToOrchestrator: () => void
  onExpandJudge: () => void
  onResizeJudgeStart: () => void
}

export function RightSidebar({
  width,
  peers,
  peerResponses,
  peerHeights,
  judgeResult,
  judgeAutoSend,
  judgeThreshold,
  judgeNotes,
  isEditingThreshold,
  approvalSent,
  isOrchestrating,
  draggedPeer,
  currentProject,
  messages,
  isCollapsed,
  onToggleCollapse,
  onTogglePeerCollapse,
  onTogglePeerEnabled,
  onOpenPeerSettings,
  onCopyPeerResponse,
  onInflatePeerResponse,
  onSavePeerChat,
  onSendPeerToOrchestrator,
  onExpandPeer,
  onPeerDragStart,
  onPeerDrop,
  onPeerDragEnd,
  onResizePeerStart,
  onToggleJudgeAutoSend,
  onSetJudgeThreshold,
  onSetJudgeNotes,
  onSetEditingThreshold,
  onApproveJudge,
  onRequestRevisions,
  onCopyJudge,
  onInflateJudge,
  onSaveJudge,
  onSendJudgeToOrchestrator,
  onExpandJudge,
  onResizeJudgeStart,
}: RightSidebarProps) {
  return (
    <aside
      style={{ width: `${width}px` }}
      className="bg-background flex flex-col overflow-hidden flex-shrink-0"
    >
      <div className="dock-header">
        <h2 className="text-sm font-title">PEER REVIEW and JUDGMENT LLMs</h2>
        <button
          onClick={onToggleCollapse}
          className="ml-auto p-1 hover:bg-background-hover rounded transition-colors"
          title="Hide peer review panel"
        >
          <ChevronDown className="w-4 h-4 transform rotate-90" />
        </button>
      </div>

      <div className="flex-1 overflow-auto p-3 space-y-2">
        {/* Render dynamic peers from project settings */}
        {peers.map((peer) => {
          const peerResponse = peerResponses[peer.id]
          const height = peerHeights[peer.id] || 200

          return (
            <PeerPanel
              key={peer.id}
              peer={peer}
              peerResponse={peerResponse}
              height={height}
              isOrchestrating={isOrchestrating}
              draggedPeer={draggedPeer}
              onToggleCollapse={() => onTogglePeerCollapse(peer.id)}
              onToggleEnabled={() => onTogglePeerEnabled(peer.id)}
              onOpenSettings={() => onOpenPeerSettings(peer.id)}
              onCopy={() => onCopyPeerResponse(peerResponse?.content || '')}
              onInflate={() => onInflatePeerResponse(peer.personaName, peerResponse?.content || '')}
              onSave={() => onSavePeerChat(peer.id, peer.personaName, peerResponse?.content || '')}
              onSendToOrchestrator={() => onSendPeerToOrchestrator(peer.personaName, peerResponse?.content || '')}
              onExpand={() => onExpandPeer(peer.id)}
              onDragStart={(e) => {
                if (!peer.enabled) return
                onPeerDragStart(peer.id)
                e.dataTransfer.effectAllowed = 'move'
              }}
              onDragOver={(e) => {
                e.preventDefault()
                e.dataTransfer.dropEffect = 'move'
              }}
              onDrop={(e) => {
                e.preventDefault()
                if (draggedPeer && draggedPeer !== peer.id) {
                  onPeerDrop(peer.id, draggedPeer)
                }
                onPeerDragEnd()
              }}
              onDragEnd={onPeerDragEnd}
              onResizeStart={() => onResizePeerStart(peer.id)}
            />
          )
        })}

        {/* Judge Panel */}
        <JudgePanel
          height={peerHeights.judge || 200}
          judgeModel={currentProject?.settings?.models?.judge}
          judgeResult={judgeResult}
          judgeAutoSend={judgeAutoSend}
          judgeThreshold={judgeThreshold}
          judgeNotes={judgeNotes}
          isEditingThreshold={isEditingThreshold}
          approvalSent={approvalSent}
          isOrchestrating={isOrchestrating}
          peerResponses={peerResponses}
          messages={messages}
          projectName={currentProject?.name}
          onToggleAutoSend={onToggleJudgeAutoSend}
          onSetThreshold={onSetJudgeThreshold}
          onSetNotes={onSetJudgeNotes}
          onSetEditingThreshold={onSetEditingThreshold}
          onApprove={onApproveJudge}
          onRequestRevisions={onRequestRevisions}
          onCopy={onCopyJudge}
          onInflate={onInflateJudge}
          onSave={onSaveJudge}
          onSendToOrchestrator={onSendJudgeToOrchestrator}
          onExpand={onExpandJudge}
          onResizeStart={onResizeJudgeStart}
        />
      </div>
    </aside>
  )
}

